import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useState, useEffect } from 'react';
import FormHelpText from './form-helpText';
import { classNames } from '@repo/ui/src/utils/classNames';

interface EnumOption {
  optionTitle: string;
}

interface FormSelectItem {
  title?: string;
  required?: boolean;
  enumOptions?: Array<EnumOption>;
  validationMessage?: string;
  helpText?: string;
}

interface FormSelectProps {
  item: FormSelectItem;
  unique: string;
  submitted: boolean;
}

const FormSelect = ({ item, unique, submitted }: FormSelectProps) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const validate = (val: string) => {
    setValue(val);
    if (item.required && val === '') {
      setError(true);
    } else {
      setError(false);
    }
  };

  useEffect(() => {
    if (submitted) {
      validate(value);
    }
  }, [submitted, value]);

  return (
    <>
      <FormGroup className="py-4">
        <InputLabel className="font-normal text-[#42474a] tra-text-web-body-m">{`${
          item.title
        }${item.required ? ' *' : ''}`}</InputLabel>
        <FormControl fullWidth error={error}>
          <Select
            native
            id={`form-${unique}`}
            name={unique}
            title={unique}
            value={value}
            variant="outlined"
            inputProps={{
              title: `${item.title}${item.required ? ' *' : ''}`,
            }}
            required={item.required}
            onChange={(e) => validate(e.target.value as string)}
            aria-describedby={`${unique}-help`}
            aria-labelledby={`${unique}-help`}
            className={classNames(
              'bg-white',
              error ? 'shadow-formError focus:shadow-formError' : 'focus:shadow-form',
            )}
            endAdornment={
              <InputAdornment className="pr-6" position="end">
                {error ? (
                  <CancelIcon color="error" />
                ) : value === '' ? (
                  ''
                ) : (
                  <CheckCircleIcon color="primary" />
                )}
              </InputAdornment>
            }
          >
            <option key="" value="">
              Select One
            </option>
            {item.enumOptions
              ? item.enumOptions.map((option) => (
                  <option
                    key={`option-${unique}-${option.optionTitle
                      .toLowerCase()
                      .replace(/[^\w]/gi, '_')}`}
                    value={option.optionTitle}
                  >
                    {option.optionTitle}
                  </option>
                ))
              : null}
          </Select>
        </FormControl>
        <FormHelperText
          id={`${unique}-help`}
          className={classNames('mt-1 text-red-400', error ? 'block' : 'hidden')}
        >
          {error
            ? item.validationMessage
              ? item.validationMessage
              : `${item.title} is required`
            : ''}
        </FormHelperText>
        <FormHelpText
          display={!!item.helpText}
          unique={unique}
          content={item.helpText !== undefined ? item.helpText : ''}
        />
      </FormGroup>
    </>
  );
};

export default FormSelect;
